import { TranslationKey } from '../../../../../types/translation/TranslationKey';

import React, { useContext, useEffect, useState } from 'react';
import { t } from '../../../../../types/translation/Translator';
import { Grid } from '@mui/material';
import { OrderExportConfigurationContext } from '../../../../../context/OrderExportConfigurationContext';
import {
  CsvExportFile,
  ReportMutations,
  ExportOrderResponse,
  ExportOrderVariables,
} from '../../../../../graphql/export.graphql';
import { useMutation } from '@apollo/client';
import moment from 'moment';
import { CompanyContext } from '../../../../../context/CompanyContext';

import { zipAndDownloadExportFiles } from '../../../../../util/export.util';
import Modal from '../../../../../VentoryUI/components/common/Modal/Modal';
import ModalPane from '../../../../../VentoryUI/components/common/Modal/components/ModalPane';
import Button from '../../../../../VentoryUI/components/common/Button';
import { ExportItems } from '../../../../components/ExportItems';
import { Routing } from '../../../../../util/routes.util';

interface OrderExportModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  ids: Set<string>;
}

export function OrderExportModal({ open, setOpen, ids }: OrderExportModalProps) {
  const { orderExportConfigurations, orderExportConfigurationsLoading } = useContext(OrderExportConfigurationContext);
  const { currentCompany } = useContext(CompanyContext);

  const [error, setError] = useState<string>('');
  const [selected, setSelected] = useState<Set<string>>(
    new Set([...orderExportConfigurations.values()].filter(config => config.default).map(config => config.id)),
  );

  useEffect(() => {
    if (!orderExportConfigurationsLoading)
      setSelected(
        new Set([...orderExportConfigurations.values()].filter(config => config.default).map(config => config.id)),
      );
  }, [orderExportConfigurationsLoading]);

  const handleClose = () => {
    setError('');
    setOpen(false);
  };

  const [download, { loading: downloadLoading }] = useMutation<ExportOrderResponse, ExportOrderVariables>(
    ReportMutations.order,
  );

  const handleDownload = async () => {
    const files: CsvExportFile[] = [];
    let hasNext = true;
    let page = 0;

    while (hasNext) {
      const response = await download({
        variables: {
          orderIds: [...ids.values()],
          page: page,
          timezone: moment.tz.guess(),
          locale: (window.navigator as any)['userLanguage'] || window.navigator.language,
          companyId: currentCompany.id,
          configurationIds: [...selected.values()],
        },
      });

      files.push(...(response.data?.generateOrderExport.data || []));

      hasNext = response.data?.generateOrderExport.hasNext || false;
      page++;
    }

    if (!files.length) return console.error('No files to download');
    zipAndDownloadExportFiles(files, orderExportConfigurations, `ventory_orders_${new Date().toISOString()}.zip`);

    handleClose();
    setSelected(new Set());
  };

  return (
    <Modal open={open} error={error} onClose={handleClose} title={t(TranslationKey.exportOrders).singular.label}>
      <ModalPane
        footer={
          <Grid container columnSpacing={1} justifyContent={'flex-end'}>
            <Grid item>
              <Button
                onClick={() => handleClose()}
                text={t(TranslationKey.cancel).singular.label}
                disabled={downloadLoading}
              />
            </Grid>
            <Grid item>
              <Button
                style='secondary'
                onClick={handleDownload}
                text={t(TranslationKey.export).singular.label}
                loading={downloadLoading}
              />
            </Grid>
          </Grid>
        }
      >
        <Grid container alignContent={'space-between'}>
          <Grid item xs={12}>
            <Grid container rowSpacing={1}>
              <Grid item xs={12}>
                <p className='text-sm font-semibold'>
                  {'Please select the configuration(s) you would like to use to export the selected order(s)'}
                </p>
              </Grid>
              <Grid item xs={12} mb={1}>
                <ExportItems
                  items={[...orderExportConfigurations.values()]}
                  selected={selected}
                  setSelected={setSelected}
                  createPath={Routing.operations.orders.settings.report.create()}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </ModalPane>
    </Modal>
  );
}
